import { BaseApiService } from '@/shared/api/services/base.service';
import { buildUrl } from '@/shared/lib/query-params';
import type {
  TokenPackagesResponse,
  TokenBalanceResponse,
  TokenCheckoutRequest,
  TokenPurchaseResponse,
  TokenPurchasesResponse,
} from '@/types/tokens';

class TokensService extends BaseApiService {
  async getPackages(): Promise<TokenPackagesResponse> {
    return this.httpGet<TokenPackagesResponse>('/v1/tokens/packages');
  }

  async getBalance(): Promise<TokenBalanceResponse> {
    return this.httpGet<TokenBalanceResponse>('/v1/tokens/balance');
  }

  async getPurchaseHistory(params?: {
    page?: number;
    per_page?: number;
  }): Promise<TokenPurchasesResponse> {
    const url = buildUrl('/v1/tokens', {
      page: params?.page ? { number: params.page } : undefined,
      per_page: params?.per_page,
    });
    return this.httpGet<TokenPurchasesResponse>(url);
  }

  async getPurchase(purchaseId: string): Promise<TokenPurchaseResponse> {
    return this.httpGet<TokenPurchaseResponse>(`/v1/tokens/${purchaseId}`);
  }

  // NO RETRY for checkout operations - dangerous for payment flows
  async createCheckoutSession(
    packageId: string,
    options?: {
      gateway?: string;
      successUrl?: string;
      cancelUrl?: string;
      paymentMethodId?: string;
      promoCode?: string;
    }
  ): Promise<string> {
    const request: TokenCheckoutRequest = {
      data: {
        type: 'checkout_session',
        attributes: {
          package_id: packageId,
          gateway: options?.gateway || null,
          success_url:
            options?.successUrl ||
            `${window.location.origin}/account/tokens/success`,
          cancel_url:
            options?.cancelUrl ||
            `${window.location.origin}/account/tokens/failure`,
          payment_method_id: options?.paymentMethodId || null,
          promo_code: options?.promoCode || null,
        },
      },
    };
    const response = await this.post<{
      data: {
        type: string;
        id: string | null;
        attributes: {
          checkout_url: string;
          session_id: string;
          purchase_id: number;
          payment_id: number;
          provider: string;
          expires_at: string | null;
        };
      };
    }>('/v1/tokens/checkout-session', request as unknown as Record<string, unknown>);
    return response.data.attributes.checkout_url;
  }

  async syncPaymentStatus(): Promise<{
    data: {
      type: string;
      attributes: {
        status: string;
        message: string;
        synced_count: number;
        failed_count?: number;
        pending_count?: number;
        current_balance?: number;
      };
    };
  }> {
    return this.post('/v1/tokens/sync-payment-status', {});
  }

  async checkPaymentStatus(paymentId: string): Promise<{
    data: {
      type: string;
      id: string;
      attributes: {
        status: 'pending' | 'completed' | 'failed' | 'refunded';
        payment_status: string;
        token_purchase_status: string;
        token_amount: number;
        current_balance: number;
        price: number;
        currency: string;
        created_at: string;
        completed_at: string | null;
      };
    };
  }> {
    return this.httpGet(`/v1/tokens/payment/${paymentId}/status`);
  }
}

export const tokensService = new TokensService();
